import { useEffect, useRef, useState } from 'react';

const getDistance = (touches) => {
  const x = touches[0].clientX - touches[1].clientX;
  const y = touches[0].clientY - touches[1].clientY;
  return Math.hypot(x, y);
};

const getCenterX = (touches) => (touches[0].clientX + touches[1].clientX) / 2;

const usePinchZoom = (ref, onZoom, threshold = 24) => {
  const [pinching, setPinching] = useState(false);
  const onZoomRef = useRef(onZoom);
  const lastDistanceRef = useRef(0);
  const accumulatedDeltaRef = useRef(0);

  useEffect(() => {
    onZoomRef.current = onZoom;
  }, [onZoom]);

  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;

    const resetGesture = () => {
      lastDistanceRef.current = 0;
      accumulatedDeltaRef.current = 0;
      setPinching(false);
    };

    const onTouchStart = (event) => {
      if (event.touches.length !== 2) return;
      event.preventDefault();
      lastDistanceRef.current = getDistance(event.touches);
      accumulatedDeltaRef.current = 0;
      setPinching(true);
    };

    const onTouchMove = (event) => {
      if (event.touches.length !== 2 || !lastDistanceRef.current) return;
      event.preventDefault();

      const distance = getDistance(event.touches);
      accumulatedDeltaRef.current += distance - lastDistanceRef.current;
      lastDistanceRef.current = distance;

      if (Math.abs(accumulatedDeltaRef.current) >= threshold) {
        onZoomRef.current?.({
          direction: accumulatedDeltaRef.current > 0 ? 1 : -1,
          centerX: getCenterX(event.touches)
        });
        accumulatedDeltaRef.current = 0;
      }
    };

    const onTouchEnd = (event) => {
      if (event.touches.length < 2) resetGesture();
    };

    element.addEventListener('touchstart', onTouchStart, { passive: false });
    element.addEventListener('touchmove', onTouchMove, { passive: false });
    element.addEventListener('touchend', onTouchEnd, { passive: true });
    element.addEventListener('touchcancel', resetGesture, { passive: true });

    return () => {
      element.removeEventListener('touchstart', onTouchStart);
      element.removeEventListener('touchmove', onTouchMove);
      element.removeEventListener('touchend', onTouchEnd);
      element.removeEventListener('touchcancel', resetGesture);
    };
  }, [ref, threshold]);

  return { pinching };
};

export default usePinchZoom;
